import { useMemo } from "react";
import { compact } from "lodash";
import { t } from "@/i18n-js/instance";
import type { Paywall } from "@/react/types/Paywall";
import { useCurrentSpaceContext } from "@circle-react/contexts";
import { usePaywallList } from "@circle-react/hooks/paywalls/usePaywallList";
import { PAYWALL_STATUS } from "@circle-react/types/Paywall";

interface PaywallWithInternalName extends Paywall {
  internal_name?: string;
}

export const usePaywallOptions = () => {
  const { data: space } = useCurrentSpaceContext();
  const { paywallsListQuery } = usePaywallList({
    communityMemberId: null,
    status: [PAYWALL_STATUS.ACTIVE],
    spaceIds: [space?.id],
  });

  const getPaywallAsOption = (paywall: PaywallWithInternalName) => {
    const isWebIncluded = paywall.stripe_prices_attributes.length > 0;
    const isInAppIncluded = paywall.mobile_prices_attributes.length > 0;

    const priceTypeStrings = compact([
      isWebIncluded ? t("web") : null,
      isInAppIncluded ? t("in_app") : null,
    ]);

    return {
      value: paywall.id,
      label: paywall.internal_name || paywall.display_name,
      description: t(
        "paywalls.lock_screen.sidebar.paywall_edit_form.fields.includes",
        {
          price_types: priceTypeStrings.join(", "),
          count: priceTypeStrings.length,
        },
      ),
    };
  };

  const allPaywallOptions = useMemo(
    () =>
      paywallsListQuery?.data
        ? paywallsListQuery.data.map(getPaywallAsOption)
        : [],
    [paywallsListQuery?.data],
  );
  const hasPaywalls = (paywallsListQuery?.data?.length ?? 0) > 0;

  return {
    paywallsListQuery,
    getPaywallAsOption,
    allPaywallOptions,
    hasPaywalls,
  };
};
